import { adminUsersService } from "@/services/admin-users.service";
import type { AdminFilters } from "@/types/admin.types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export function useAdminUsers(filters: AdminFilters = {}) {
  return useQuery({
    queryKey: ["admin-users", filters],
    queryFn: () => adminUsersService.getAll(filters),
  });
}

export function useAdminUserDetail(uuid: string | undefined) {
  return useQuery({
    queryKey: ["admin-user", uuid],
    queryFn: () => adminUsersService.getById(uuid!),
    enabled: !!uuid,
  });
}

export function useToggleUserLock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminUsersService.toggleLock(uuid),
    onSuccess: () => {
      toast.success("Statut utilisateur mis à jour");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (uuid: string) => adminUsersService.resetPassword(uuid),
    onSuccess: () => toast.success("Email de réinitialisation envoyé"),
    onError: () => toast.error("Erreur lors de la réinitialisation"),
  });
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: (uuid: string) => adminUsersService.impersonate(uuid),
    onSuccess: (data: any) => {
      const token = data.accessToken || data.token;
      const clientAppUrl = "https://app.innov-paie.fr";
      window.open(`${clientAppUrl}?impersonate=${token}`, "_blank");
      toast.success("Session d'impersonation ouverte dans un nouvel onglet");
    },
    onError: () => toast.error("Erreur lors de l'impersonation"),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => adminUsersService.deleteUser(uuid),
    onSuccess: () => {
      toast.success("Utilisateur supprimé");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, role }: { uuid: string; role: string }) =>
      adminUsersService.updateRole(uuid, role),
    onSuccess: () => {
      toast.success("Rôle mis à jour");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: () => toast.error("Erreur lors de la mise à jour du rôle"),
  });
}
